import React, { JSX, useState } from 'react';
import EvalModeBanner from '../../common/EvalModeBanner';
import { SignInFormData } from '../../../types/auth';
import termsDocumentUrl from '../../../assets/docs/pelora-terms-of-service.md';
import privacyDocumentUrl from '../../../assets/docs/pelora-privacy-policy.md';
import TurnstileWidget from '../TurnstileWidget';
import PolicyDocumentDialog, { type PolicyDocument } from '../PolicyDocumentDialog';

interface SignInFormProps {
  signinData: SignInFormData;
  handleSigninChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSignin: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleForgotPassword: () => void;
  setView: (view: string) => void;
  error: string | null;
  /** Success message (email verified, password reset, ...) */
  notice?: string | null;
  isLoading: boolean;
  isLoadingPermissions?: boolean;
  renderOrgIdInput?: () => JSX.Element | null;
  /** Show the Turnstile CAPTCHA (after 3 failed sign-ins); Sign in stays disabled until it is solved. */
  captchaRequired?: boolean;
  captchaToken?: string | null;
  onCaptchaToken?: (token: string | null) => void;
  /** Changed after every attempt so the widget issues a new single-use token. */
  captchaResetKey?: number;
}

const SignInForm: React.FC<SignInFormProps> = ({
  signinData,
  handleSigninChange,
  handleSignin,
  handleForgotPassword,
  setView,
  error,
  notice = null,
  isLoading,
  isLoadingPermissions,
  renderOrgIdInput,
  captchaRequired = false,
  captchaToken = null,
  onCaptchaToken,
  captchaResetKey = 0
}) => {
  const awaitingCaptcha = captchaRequired && !captchaToken;
  const submitDisabled = isLoading || Boolean(isLoadingPermissions) || awaitingCaptcha;
  const [policy, setPolicy] = useState<PolicyDocument | null>(null);

  // Handle form submission (used for both button click and Enter key press)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create a synthetic MouseEvent to pass to the existing handleSignin function
    const syntheticEvent = {} as React.MouseEvent<HTMLButtonElement>;
    handleSignin(syntheticEvent);
  };
  
  return (
    <div className="w-full max-w-sm py-8 md:py-10">
      {/* Demo/Evaluation Mode Disclaimer Banner (demo build only) */}
      <EvalModeBanner />

      <h2 className="text-2xl font-semibold mb-1 text-slate-800 text-left">Sign in to your account</h2>
      <p className="text-gray-500 mb-6 text-sm text-left">Enter your credentials to access your dashboard</p>
      
      {error && (
        <div className="p-3 bg-red-500/10 border-red-500 text-red-500 border-l-4 rounded mb-6 text-sm" role="alert">
          {error}
        </div>
      )}
      {!error && notice && (
        <div className="p-3 bg-green-500/10 border-green-500 text-green-600 border-l-4 rounded mb-6 text-sm" role="status">
          {notice}
        </div>
      )}
      
      <form onSubmit={handleSubmit} autoComplete="off" className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="email">Email address</label>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="email"
            id="email"
            name="email"
            autoComplete="off"
            placeholder="name@practice.com"
            value={signinData.email}
            onChange={handleSigninChange}
          />
        </div>
        
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="font-medium text-slate-800 text-sm" htmlFor="password">Password</label>
            <button 
              type="button" 
              className="text-blue-600 text-sm hover:text-blue-700"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          </div>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="password"
            id="password"
            name="password"
            autoComplete="off"
            placeholder="Enter your password"
            value={signinData.password}
            onChange={handleSigninChange}
          />
        </div>
        
        {renderOrgIdInput && renderOrgIdInput()}

        {captchaRequired && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-slate-600 text-left">Complete the verification to continue.</p>
            <TurnstileWidget onToken={(token) => onCaptchaToken?.(token)} resetKey={captchaResetKey} />
          </div>
        )}
        
        <div className="mb-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="rememberMe"
              checked={signinData.rememberMe}
              onChange={handleSigninChange}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-20"
            />
            <span className="ml-2 text-sm text-gray-600">Remember me</span>
          </label>
        </div>
        <button 
          type="submit"
          className={`w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm font-medium transition flex items-center justify-center gap-2 ${submitDisabled ? 'opacity-70 cursor-not-allowed' : ''}`}
          disabled={submitDisabled}
        >
          {isLoadingPermissions && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          {isLoading && !isLoadingPermissions ? 'Signing in...' : isLoadingPermissions ? 'Loading permissions...' : 'Sign in'}
        </button>
      </form>
      
      <div className="mt-6 text-center text-sm">
        <p>
          Don't have an account? {' '}
          <button 
            type="button"
            onClick={() => setView('signup')} 
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Sign up
          </button>
        </p>
      </div>
      
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>
          By signing in, you agree to our{' '}
          <button type="button" onClick={() => setPolicy({ title: 'Terms of Service', url: termsDocumentUrl })} className="text-blue-600 hover:text-blue-700 underline">
            Terms of Service
          </button>{' '}
          and{' '}
          <button type="button" onClick={() => setPolicy({ title: 'Privacy Policy', url: privacyDocumentUrl })} className="text-blue-600 hover:text-blue-700 underline">
            Privacy Policy
          </button>
        </p>
      </div>
      
      <div className="mt-8 p-4 border border-slate-200 rounded-md bg-slate-50">
        <p className="text-sm text-gray-600 my-1">Need help? Contact support at support@pelorahealth.com</p>
      </div>

      <PolicyDocumentDialog policy={policy} onClose={() => setPolicy(null)} />
    </div>
  );
};

export default SignInForm;
